import React, { useEffect, useRef } from 'react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { Box, Typography, Paper } from '@mui/material';
import { useTranslation } from 'react-i18next';

const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN;
if (MAPBOX_TOKEN && !mapboxgl.accessToken) {
  mapboxgl.accessToken = MAPBOX_TOKEN;
}

/**
 * Lean map view for Route Setup. Accepts:
 *   stops      - [{ id, priority, latitude, longitude, title, subtitle }]
 *   geometry   - optional GeoJSON LineString / Feature returned by Mapbox
 *                optimization; renders as a polyline between stops.
 *   depot      - optional { latitude, longitude, name } to mark start/end.
 *   height     - optional CSS height (default 360)
 *   geocoding  - optional boolean; when true AND no stops have coords yet,
 *                shows a "Geocoding addresses..." overlay. Once any stop has
 *                coords the overlay is hidden regardless.
 *
 * Designed to be small, read-only, and safe to drop into a Dialog.
 */
const RouteMap = ({ stops = [], geometry, depot, height = 360, geocoding = false }) => {
  const { t } = useTranslation();
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const markersRef = useRef([]);

  // Initialise the map once.
  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;
    if (!MAPBOX_TOKEN) return;

    mapRef.current = new mapboxgl.Map({
      container: containerRef.current,
      style: 'mapbox://styles/mapbox/streets-v12',
      center: [-79.3832, 43.6532],
      zoom: 10,
      attributionControl: true,
    });
    mapRef.current.addControl(new mapboxgl.NavigationControl(), 'top-right');

    return () => {
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
    };
  }, []);

  // Redraw whenever stops, geometry, or depot change.
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    const draw = () => {
      // Clear existing markers.
      markersRef.current.forEach((m) => m.remove());
      markersRef.current = [];

      // Remove existing route layer + source.
      if (map.getLayer('route-line')) map.removeLayer('route-line');
      if (map.getSource('route-line')) map.removeSource('route-line');

      const positions = [];

      // Add depot marker (distinct colour).
      if (depot && depot.latitude != null && depot.longitude != null) {
        const el = document.createElement('div');
        el.style.cssText = 'width:28px;height:28px;border-radius:50%;background:#000062;border:3px solid white;box-shadow:0 1px 4px rgba(0,0,0,0.35);display:flex;align-items:center;justify-content:center;color:white;font-weight:700;font-size:11px;';
        el.textContent = 'D';
        el.setAttribute('aria-label', depot.name || 'Depot');
        const marker = new mapboxgl.Marker(el)
          .setLngLat([parseFloat(depot.longitude), parseFloat(depot.latitude)])
          .setPopup(new mapboxgl.Popup({ offset: 24 }).setText(depot.name || 'Depot'))
          .addTo(map);
        markersRef.current.push(marker);
        positions.push([parseFloat(depot.longitude), parseFloat(depot.latitude)]);
      }

      // Numbered stop markers.
      stops.forEach((s, i) => {
        if (s.latitude == null || s.longitude == null) return;
        const lng = parseFloat(s.longitude);
        const lat = parseFloat(s.latitude);
        if (Number.isNaN(lng) || Number.isNaN(lat)) return;

        const el = document.createElement('div');
        el.style.cssText = 'width:30px;height:30px;border-radius:50%;background:#de6127;border:3px solid white;box-shadow:0 1px 4px rgba(0,0,0,0.35);display:flex;align-items:center;justify-content:center;color:white;font-weight:700;font-size:13px;';
        el.textContent = String(s.priority || i + 1);
        el.setAttribute('aria-label', `Stop ${s.priority || i + 1}: ${s.title || ''}`);

        const popupHtml = `
          <div style="font-family:Arial,Helvetica,sans-serif;min-width:160px;">
            <div style="font-weight:700;margin-bottom:2px;">#${s.priority || i + 1} ${escapeHtml(s.title || '')}</div>
            ${s.subtitle ? `<div style="font-size:12px;color:#555;">${escapeHtml(s.subtitle)}</div>` : ''}
          </div>
        `;
        const marker = new mapboxgl.Marker(el)
          .setLngLat([lng, lat])
          .setPopup(new mapboxgl.Popup({ offset: 24 }).setHTML(popupHtml))
          .addTo(map);
        markersRef.current.push(marker);
        positions.push([lng, lat]);
      });

      // Route polyline.
      const line = extractLineString(geometry);
      if (line) {
        map.addSource('route-line', { type: 'geojson', data: line });
        map.addLayer({
          id: 'route-line',
          type: 'line',
          source: 'route-line',
          layout: { 'line-join': 'round', 'line-cap': 'round' },
          paint: {
            'line-color': '#000062',
            'line-width': 4,
            'line-opacity': 0.75,
          },
        });
      }

      // Fit bounds.
      if (positions.length >= 2) {
        const bounds = positions.reduce(
          (b, c) => b.extend(c),
          new mapboxgl.LngLatBounds(positions[0], positions[0])
        );
        map.fitBounds(bounds, { padding: 48, maxZoom: 14, duration: 350 });
      } else if (positions.length === 1) {
        map.easeTo({ center: positions[0], zoom: 13, duration: 350 });
      }
    };

    if (map.isStyleLoaded()) draw();
    else map.once('load', draw);

    // Dialogs mount their children before they reach final size. A late
    // resize() plus a second on next frame makes sure the map fills the
    // container once the Dialog transition finishes.
    const t1 = setTimeout(() => { try { map.resize(); } catch (_) {} }, 120);
    const t2 = setTimeout(() => { try { map.resize(); } catch (_) {} }, 400);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [stops, geometry, depot]);

  if (!MAPBOX_TOKEN) {
    return (
      <Paper variant="outlined" sx={{ p: 3, textAlign: 'center' }}>
        <Typography color="text.secondary" variant="body2">
          {t('routeSetup.mapTokenMissing')}
        </Typography>
      </Paper>
    );
  }

  const hasGeocoded = stops.some((s) => s.latitude != null && s.longitude != null);
  const showGeocodingBanner = geocoding && !hasGeocoded && stops.length > 0;

  // The height must live on the OUTER (wrapping) element. mapbox-gl adds the
  // class `.mapboxgl-map` to its container, and App.css has a global
  //   .mapboxgl-map { height: 100% !important; }
  // rule that would otherwise collapse an inline-sized container to 0.
  // Putting the height here makes "100%" resolve to the value we want.
  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        height,
        minHeight: 300,
        borderRadius: 1,
        overflow: 'hidden',
        border: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Box
        ref={containerRef}
        aria-label={t('routeSetup.mapAria')}
        sx={{ width: '100%', height: '100%' }}
      />
      {showGeocodingBanner && (
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            backgroundColor: 'background.paper',
            px: 2,
            py: 1,
            borderRadius: 1,
            boxShadow: 2,
          }}
        >
          <Typography variant="body2" color="text.secondary">
            {t('routeSetup.mapGeocoding')}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

function extractLineString(geometry) {
  if (!geometry) return null;
  // Accept raw GeoJSON LineString, a Feature wrapping one, or a bare coords array.
  if (geometry.type === 'Feature' && geometry.geometry) {
    return geometry;
  }
  if (geometry.type === 'LineString' && Array.isArray(geometry.coordinates)) {
    return { type: 'Feature', geometry, properties: {} };
  }
  if (Array.isArray(geometry) && Array.isArray(geometry[0])) {
    return {
      type: 'Feature',
      geometry: { type: 'LineString', coordinates: geometry },
      properties: {},
    };
  }
  return null;
}

function escapeHtml(str) {
  if (str == null) return '';
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

export default RouteMap;
